import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";

const PaymentSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshSubscription, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const confirmPayment = async () => {
      if (!sessionId) {
        setStatus("error");
        setErrorMessage("Session ID mancante");
        return;
      }

      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          // User not logged in - redirect to login
          navigate("/auth/login?returnUrl=/payment-success?session_id=" + sessionId);
          return;
        }

        const { data, error } = await supabase.functions.invoke("confirm-checkout-session", {
          body: { sessionId },
        });

        if (error) {
          console.error("Error confirming checkout:", error);
          setStatus("error");
          setErrorMessage(error.message || "Errore durante la conferma del pagamento");
          return;
        }

        if (data?.success) {
          setStatus("success");
          await refreshSubscription();
          toast({
            title: "Piano attivato!",
            description: data.message || "Il tuo piano è stato attivato con successo.",
          });
          
          // Redirect to dashboard after 2 seconds
          setTimeout(() => {
            navigate("/dashboard");
          }, 2000);
        } else {
          setStatus("error");
          setErrorMessage(data?.error || "Errore sconosciuto");
        }
      } catch (err: any) {
        console.error("Confirm payment error:", err);
        setStatus("error");
        setErrorMessage(err.message || "Errore durante la conferma del pagamento");
      }
    };

    confirmPayment();
  }, [sessionId, navigate, refreshSubscription]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 flex items-center justify-center py-12">
        <div className="max-w-md w-full mx-auto px-4">
          <div className="bg-card border-4 border-foreground shadow-brutal p-8 text-center">
            {status === "loading" && (
              <>
                <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin text-primary" />
                <h1 className="text-2xl font-bold mb-2">Conferma pagamento...</h1>
                <p className="text-muted-foreground">
                  Stiamo verificando il tuo pagamento. Attendere prego.
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
                <h1 className="text-2xl font-bold mb-2">Pagamento confermato!</h1>
                <p className="text-muted-foreground mb-6">
                  Il tuo piano è stato attivato con successo. Verrai reindirizzato alla dashboard.
                </p>
                <BrutalButton onClick={() => navigate("/dashboard")}>
                  Vai alla Dashboard
                </BrutalButton>
              </>
            )}

            {status === "error" && (
              <>
                <XCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
                <h1 className="text-2xl font-bold mb-2">Errore</h1>
                <p className="text-muted-foreground mb-6">
                  {errorMessage || "Si è verificato un errore durante la conferma del pagamento."}
                </p>
                <div className="space-y-3">
                  <BrutalButton onClick={() => navigate("/pricing")} className="w-full">
                    Torna ai Piani
                  </BrutalButton>
                  <BrutalButton 
                    variant="outline" 
                    onClick={() => navigate("/contact")} 
                    className="w-full"
                  >
                    Contatta Supporto
                  </BrutalButton>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
